import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { getPatientFiltersAction } from 'src/redux/actions/documentBundleActions';
import { Patient } from '../mock-data';

interface Filters {
  dateFrom: Date;
  dateTo: Date;
  payerType: string;
  payerName: string;
  disciplines: string[];
  docTypes: string[];
  dateScope: string;
  therapistId?: string;
  therapistScope?: string;
}

export function usePatientList(filters: Filters) {
  const dispatch = useDispatch();
  const { response, loading } = useSelector((state: any) => state.getPatientFiltersReducer);

  useEffect(() => {
    const params: Record<string, string | string[]> = {
      date_from: format(filters.dateFrom, 'yyyy-MM-dd'),
      date_to: format(filters.dateTo, 'yyyy-MM-dd'),
    };
    if (filters.payerType) params.payer_type = filters.payerType;
    if (filters.payerName) params.payer_name = filters.payerName;
    if (filters.disciplines.length > 0) params.discipline = filters.disciplines;
    if (filters.docTypes.length > 0) params.doc_type = filters.docTypes;
    if (filters.dateScope && filters.dateScope !== 'range') params.date_scope = filters.dateScope;
    if (filters.therapistId) {
      params.therapist_id = filters.therapistId;
      if (filters.therapistScope) params.therapist_scope = filters.therapistScope;
    }

    dispatch(getPatientFiltersAction(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.payerType,
    filters.payerName,
    filters.dateScope,
    filters.therapistId,
    filters.therapistScope,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.disciplines),
    JSON.stringify(filters.docTypes),
  ]);

  // Stable reference: only re-derive when the underlying response changes.
  // Reference identity matters — the consuming component runs effects on `patients`,
  // and an unstable ref would re-fire those effects on every render and stomp user state
  // (deselection, search filter intent).
  const patients: Patient[] = useMemo(
    () =>
      (response ?? []).map((r: any) => ({
        patient_id: r.uuid,
        mrn: r.mrn ?? '',
        first_name: r.firstName,
        last_name: r.lastName,
        document_count: r.documentCount ?? 0,
      })),
    [response]
  );

  return { patients, loading };
}
